/**
 * Adaptadores CONSUMO: convierten las filas `block` de PowerSync (donde la
 * `content` viaja como JSON string) en los `AuthoredBlock` del motor
 * `learning-blocks`, y corren la corrección local-first de una lección.
 */

import type { AuthoredBlock, BlockResponse } from "../learning-blocks/types";
import type { BlockRegistry } from "../learning-blocks/registry";
import {
  buildAttemptSubmission,
  gradeLessonAttempt,
  toLessonOutcome,
  type LessonOutcome,
} from "./progress";

/** Fila `block` tal como la lee el Drizzle/PowerSync local. */
export interface BlockRow {
  id: string;
  kind: string | null;
  content: unknown;
  weight: number | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Parsea la `content` (string JSON o ya objeto) a la `def` de autoría. */
export function parseBlockContent(content: unknown): Record<string, unknown> {
  if (content == null) {
    return {};
  }
  if (typeof content === "string") {
    try {
      const parsed: unknown = JSON.parse(content);
      return isRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return isRecord(content) ? content : {};
}

/** Convierte una fila `block` en un `AuthoredBlock` del motor. */
export function authoredBlockFromRow(row: BlockRow): AuthoredBlock {
  return {
    id: row.id,
    type: row.kind ?? "quiz",
    def: parseBlockContent(row.content),
    weight: row.weight ?? 1,
  };
}

export function authoredBlocksFromRows(rows: readonly BlockRow[]): AuthoredBlock[] {
  return rows.map(authoredBlockFromRow);
}

/**
 * Corrige una lección local-first (misma lógica que re-verifica el servidor) y
 * produce el `LessonOutcome` para la UI + persistencia + gamificación.
 */
export function consumeLesson(params: {
  registry: BlockRegistry;
  blocks: readonly AuthoredBlock[];
  responses: BlockResponse[];
  lessonId: string;
  courseId: string;
  xpReward: number;
  startedAt?: number;
  finishedAt?: number;
  claimedScore?: number;
  courseCompleted?: boolean;
}): LessonOutcome {
  const submission = buildAttemptSubmission(params.lessonId, params.responses, {
    claimedScore: params.claimedScore,
    startedAt: params.startedAt,
    finishedAt: params.finishedAt,
  });
  const attempt = gradeLessonAttempt(params.registry, params.blocks, submission);
  return toLessonOutcome({
    lessonId: params.lessonId,
    courseId: params.courseId,
    xpReward: params.xpReward,
    attempt,
    courseCompleted: params.courseCompleted,
  });
}
